import { Injectable } from '@angular/core';
import { ChatFixtureService } from '../../features/chat/chat/chat-fixture.service';
import { UserService } from './user.service';
import { config } from '../../../environments/environment';
import { ActivityLogFixtureService } from './activity-log-fixture.service';
import { TranscriptFixtureService } from './transcript-fixture.service';
import { RoomsFixtureService } from './rooms-fixture.service';

// Lets the browser console (and E2E tests) drive the app without going
// through a UI. Operations are reached via `window.chat.*`, `window.users.*`,
// and the top-level `window.seed()` shortcut that seeds the app with dev
// fixtures in one call.
//
// Gated on `config.exposeDevTools` (set in `environment.ts`). Production
// builds set it to false and the namespace is never installed.
//
// Instance-bound chat operations (`purgeAll`, `scrollToLatest`, …) need a
// reference to the live Chat — the chat registers itself on
// init and unregisters on destroy. If no chat is mounted those methods
// no-op with a console warning.

const DUMMY_USER_BREAKOUT_ROOMS: (string | undefined)[] = [
  undefined,
  'Gryffindor',
  'Slytherin',
  'Ravenclaw',
  'Hufflepuff',
];

/** Minimal contract a chat component must implement to be controllable
 * from `window.chat.*`. Defined here so `dev-console.service.ts` doesn't
 * have to import the heavy Chat class (and create a circular
 * dependency). */
export interface ChatConsoleTarget {
  purgeAllMessages(): void;
  toggleReadState(): void;
  scrollToLatest(): void;
  scrollToFirstUnread(): void;
}

declare global {
  interface Window {
    chat?: {
      addTestMessages(): Promise<void>;
      addStressFixture(count?: number, imageCount?: number): Promise<void>;
      purgeAll(): void;
      toggleReadState(): void;
      scrollToLatest(): void;
      scrollToFirstUnread(): void;
    };
    users?: {
      createDummyUsers(count?: number, breakoutRoomIds?: (string | undefined)[]): void;
    };
    activity?: {
      addFixture(count?: number): void;
      clear(): void;
    };
    transcript?: {
      addFixture(count?: number): void;
      clear(): void;
    };
    rooms?: {
      addFixture(count?: number): void;
      clear(): void;
    };
    seed?: () => void;
  }
}

@Injectable({ providedIn: 'root' })
export class DevConsoleService {
  private _activeChat: ChatConsoleTarget | null = null;

  constructor(
    private _chatFixture: ChatFixtureService,
    private _userService: UserService,
    private _activityLogFixture: ActivityLogFixtureService,
    private _transcriptFixture: TranscriptFixtureService,
    private _roomsFixture: RoomsFixtureService
  ) {
    if (config.exposeDevTools) {
      this._install();
    }
  }

  registerChat(chat: ChatConsoleTarget): void {
    this._activeChat = chat;
  }

  unregisterChat(chat: ChatConsoleTarget): void {
    if (this._activeChat === chat) this._activeChat = null;
  }

  private _install(): void {
    if (typeof window === 'undefined') return;
    window.chat = {
      addTestMessages: () => this._chatFixture.addTestMessages(),
      addStressFixture: (count = 1000, imageCount = 150) =>
        this._chatFixture.addStressFixture(count, imageCount),
      purgeAll: () => this._withActiveChat('purgeAll', (c) => c.purgeAllMessages()),
      toggleReadState: () => this._withActiveChat('toggleReadState', (c) => c.toggleReadState()),
      scrollToLatest: () => this._withActiveChat('scrollToLatest', (c) => c.scrollToLatest()),
      scrollToFirstUnread: () =>
        this._withActiveChat('scrollToFirstUnread', (c) => c.scrollToFirstUnread()),
    };
    window.users = {
      createDummyUsers: (count = 50, breakoutRoomIds = DUMMY_USER_BREAKOUT_ROOMS) =>
        this._userService.createDummyUsers(count, breakoutRoomIds),
    };
    window.activity = {
      addFixture: (count = 50) => this._activityLogFixture.addActivityLogFixture(count),
      clear: () => this._activityLogFixture.clear(),
    };
    window.transcript = {
      addFixture: (count = 50) => this._transcriptFixture.addTranscriptFixture(count),
      clear: () => this._transcriptFixture.clear(),
    };
    window.rooms = {
      addFixture: (count = 15) => this._roomsFixture.addRoomsFixture(count),
      clear: () => this._roomsFixture.clear(),
    };
    window.seed = () => this._seed();
    this._logCommandList();
  }

  // One-shot convenience that fills the app with everything a developer
  // typically wants when poking around: dummy peers spread across breakout
  // rooms, a stress chat fixture, plus 50 synthetic activity-log entries.
  // Intentionally no-arg — call the individual `users.*` / `chat.*` /
  // `activity.*` commands when you need different counts. Extend this as
  // more dev fixtures appear.
  private _seed(): void {
    this._userService.createDummyUsers(50, DUMMY_USER_BREAKOUT_ROOMS);
    void this._chatFixture.addStressFixture();
    this._activityLogFixture.addActivityLogFixture(50);
  }

  // Discoverability banner — printed once at app bootstrap on dev builds.
  // Collapsed by default so it doesn't clutter the console during normal
  // debugging; expand to see the full surface.
  private _logCommandList(): void {
    /* eslint-disable no-console */
    console.groupCollapsed('%c[andor] dev console commands available', 'color: #6cf');
    console.log('chat.addTestMessages()               — seed the curated test fixture');
    console.log('chat.addStressFixture(count?, imgs?) — bulk seed (default 1000 msgs / 150 imgs)');
    console.log('chat.purgeAll()                      — clear all messages from the store');
    console.log('chat.toggleReadState()               — flip every message between read / unread');
    console.log('chat.scrollToLatest()                — jump to the latest message');
    console.log('chat.scrollToFirstUnread()           — jump to the first unread message');
    console.log(
      'users.createDummyUsers(count?, rooms?) — seed dummy peers (default 50 / Hogwarts)'
    );
    console.log('activity.addFixture(count?)          — seed activity-log entries (default 50)');
    console.log('activity.clear()                     — wipe the activity log');
    console.log('transcript.addFixture(count?)        — seed transcript lines (default 50)');
    console.log('transcript.clear()                   — wipe stored transcript sessions');
    console.log(
      'rooms.addFixture(count?)             — seed dummy rooms (default 15, mixed presence)'
    );
    console.log('rooms.clear()                        — remove the dummy rooms again');
    console.log('seed()                               — one-shot: dummy peers + chat + activity');
    console.groupEnd();
    /* eslint-enable no-console */
  }

  private _withActiveChat(name: string, fn: (chat: ChatConsoleTarget) => void): void {
    if (!this._activeChat) {
      console.warn(`[devConsole] chat.${name}() called but no chat is mounted`);
      return;
    }
    fn(this._activeChat);
  }
}
